import _ from 'lodash'
import React from 'react'
import { all, put, takeEvery, takeLatest } from 'redux-saga/effects'

import { changeModal } from 'services/actions'
import { downloadCsv } from 'common/utils/download-csv'
import { downloadUserActivationLinksService } from 'services/users'
import {
  predefineDistrictUserForm,
  resetDistrictUserFormData,
} from 'page-modules/dashboard/district-details/service/actions'
import {
  predefineSchoolUserForm,
  resetSchoolUserFormData,
} from 'page-modules/dashboard/school-details/service/actions'
import {
  predefineYceiUserForm,
  resetYceiUserFormData,
} from 'page-modules/dashboard/ycei-members/service/actions'
import DistrictUserModal from 'page-modules/dashboard/district-details/district-user-modal'
import SchoolUserModal from 'page-modules/dashboard/school-details/school-users-modal'
import YceiUserModal from 'page-modules/dashboard/ycei-members/ycei-user-modal'
import personRolesService from 'services/person-roles'
import personTypesService from 'services/person-types'

import { OPEN_USER_MODAL } from './actions'

function* openDistrictUserModal(
  userData: Record<string, unknown>,
  district: Record<string, unknown>,
): Generator<unknown, void, unknown> {
  const typesAndRolesParams = { urlParams: { scope: 'district' } }
  yield put(personRolesService.actions.request(typesAndRolesParams))
  yield put(personTypesService.actions.request(typesAndRolesParams))
  if (!_.isEmpty(userData)) {
    yield put(
      predefineDistrictUserForm(
        userData as unknown as Parameters<typeof predefineDistrictUserForm>[0],
      ),
    )
  } else {
    yield put(resetDistrictUserFormData())
  }
  yield put(
    changeModal({
      isOpen: true,
      aria: {
        labelledby: 'add-new-district-user-header',
      },
      className: 'add-district-users',
      content: React.createElement(
        DistrictUserModal as unknown as React.ComponentType<Record<string, unknown>>,
        {
          edit: !_.isEmpty(userData),
          district,
        },
      ),
    }),
  )
}

function* openSchoolUserModal(
  userData: Record<string, unknown>,
  school: Record<string, unknown>,
  district: Record<string, unknown>,
): Generator<unknown, void, unknown> {
  const typesAndRolesParams = { urlParams: { scope: 'school' } }
  yield put(personRolesService.actions.request(typesAndRolesParams))
  yield put(personTypesService.actions.request(typesAndRolesParams))
  if (!_.isEmpty(userData)) {
    yield put(
      predefineSchoolUserForm(userData as unknown as Parameters<typeof predefineSchoolUserForm>[0]),
    )
  } else {
    yield put(resetSchoolUserFormData())
  }
  yield put(
    changeModal({
      isOpen: true,
      aria: {
        labelledby: 'add-new-school-user-header',
      },
      className: 'add-school-users',
      content: React.createElement(
        SchoolUserModal as unknown as React.ComponentType<Record<string, unknown>>,
        {
          edit: !_.isEmpty(userData),
          school,
          district,
        },
      ),
    }),
  )
}

function* openYceiUserModal(userData: Record<string, unknown>): Generator<unknown, void, unknown> {
  yield put(personRolesService.actions.request({ urlParams: { scope: 'ruler,ycei' } }))
  yield put(personTypesService.actions.request({ urlParams: { scope: 'ycei' } }))
  if (!_.isEmpty(userData)) {
    yield put(
      predefineYceiUserForm(userData as unknown as Parameters<typeof predefineYceiUserForm>[0]),
    )
  } else {
    yield put(resetYceiUserFormData())
  }
  yield put(
    changeModal({
      isOpen: true,
      aria: {
        labelledby: 'add-new-ycei-user-header',
      },
      className: 'add-ycei-users',
      content: React.createElement(
        YceiUserModal as unknown as React.ComponentType<Record<string, unknown>>,
        { edit: !_.isEmpty(userData) },
      ),
    }),
  )
}

interface OpenUserModalPayload {
  user?: Record<string, unknown>
  district?: Record<string, unknown>
  school?: Record<string, unknown>
}

function* openUserModalSaga({
  payload: { user = {}, district = {}, school = {} } = {},
}: {
  payload: OpenUserModalPayload
}): Generator<unknown, void, unknown> {
  let userData: Record<string, unknown> = {}
  if (!_.isEmpty(user)) {
    const u = user as Record<string, unknown>
    userData = {
      email: u.email,
      id: u.id,
      fullName: u.name,
      roleId: u.role,
      personTypeId: u.person_type_id ?? (u.person_type as { id?: string } | undefined)?.id ?? null,
      officePhone: u.office_phone,
      mobilePhone: u.mobile_phone,
    }
  }
  if (!_.isEmpty(district) && _.isEmpty(school)) {
    yield openDistrictUserModal(userData, district)
  } else if (!_.isEmpty(school)) {
    yield openSchoolUserModal(userData, school, district)
  } else {
    yield openYceiUserModal(userData)
  }
}

export function* downloadActivationLinksSaga(actionData: {
  payload: { data: string }
}): Generator<void, void, unknown> {
  const csvData = actionData.payload.data
  downloadCsv(csvData, 'ruler-member-activation-links.csv')
}

type OpenUserModalAction = { type: typeof OPEN_USER_MODAL; payload: OpenUserModalPayload }
type DownloadActivationLinksSuccessAction = {
  type: string
  payload: { data: string }
}

export default function* saga(): Generator<unknown, void, unknown> {
  yield all([
    takeEvery<OpenUserModalAction>(OPEN_USER_MODAL, openUserModalSaga),
    takeLatest<DownloadActivationLinksSuccessAction>(
      downloadUserActivationLinksService.constants.REQUEST_SUCCESS,
      downloadActivationLinksSaga,
    ),
  ])
}
